import React, { useState, useEffect, useContext, useRef } from "react";
import { motion } from "framer-motion";
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  Typography,
  Dialog,
  DialogContent,
  Avatar,
  AvatarGroup,
  Collapse,
} from "@mui/material";
import {
Modal, ModalContent, ModalHeader, ModalBody, useDisclosure
} from "@nextui-org/react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import { LuPartyPopper } from "react-icons/lu";
import confetti from "canvas-confetti";
import { FaGripfire } from "react-icons/fa";
import InfoIcon from "@mui/icons-material/Info";
import ReactLottie from "lottie-react";

import treeAnimation from "../animations/slostr_tree_v2.json";
import progressBarAnimation from "../animations/progress-bar.json";

import { AuthContext } from "../authentification/AuthContext";
import { fetchAllChallenges, joinChallenge } from "../../utils/api/api"
import Card from "../blog/CardSmall"
import CommentSection from "./CommentSection"
import CreateAccountForm from "../authentification/CreateAccountForm";


const ChallengeButton = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const {isOpen, onOpen, onOpenChange, onClose} = useDisclosure();
  const [isLoading, setIsLoading] = useState(false);
  const [isAnimLoading, setIsAnimLoading] = useState(false);

  const [showInfo, setShowInfo] = useState(false);
  const [isChallengeVisible, setIsChallengeVisible] = useState(false);
  const [animate, setAnimate] = useState(true);
  const [challenges, setChallenges] = useState(null);
  const treeLottieRef = useRef(null);
  const barLottieRef = useRef(null);
    const [selectedBadge, setSelectedBadge] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showParticipantModal, setShowParticipantModal] = useState(false);

  const getChallenges = async () => {
    try {
      const data = await fetchAllChallenges();
      setChallenges(data);
    } catch (error) {
      console.error("Error fetching GeoJSON data:", error);
    }
  };

  useEffect(() => {
    if (!isLoading) {
    onClose();
    getChallenges();
    }
  }, [isLoading]);

  const toggleChallenge = () => {
    if (!isChallengeVisible) {
      confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
    }
    setIsChallengeVisible(!isChallengeVisible);
  };

  const handleJoinChallenge = async () => {
    if (!user) {
      setIsChallengeVisible(false)
      onOpenChange()
      return
    }

      try {
        setChallenges((prevChallenges) =>
          prevChallenges.map((challenge, index) =>
            index === 0
              ? { ...challenge, participants: [...challenge.participants, user] }
              : challenge
          )
        );
      const data = await joinChallenge(challenges[0].id);
      } catch (error) {
        console.error("Error joining challenge", error);
      }
  }

  useEffect(() => {
    const timer = setTimeout(() => setAnimate(false), 15000);
    return () => clearTimeout(timer);
  }, []);

  const calculateCO2Saved = () => {
    if (!challenges) return
    let totalCarEmissions = 0;
    let totalTripEmissions = 0;

    challenges[0].participating_trips.forEach(trip => {
      if (trip.co2_stats) {
        trip.co2_stats.car.forEach(stat => {
          totalCarEmissions += stat.kgCO2eq;
        });
        trip.co2_stats.trip.forEach(stat => {
          totalTripEmissions += stat.kgCO2eq;
        });
      }
    });

    return Math.max(0, totalCarEmissions - totalTripEmissions).toFixed(0);
  };

  // const co2Saved: any = 250
  const co2Saved: any = calculateCO2Saved();

  const maxCO2 = challenges ? challenges[0].co2_obj : 1;

  useEffect(() => {
    if (isChallengeVisible) {
      const timer = setTimeout(() => {
        setIsAnimLoading(true);
        console.log('shown')
      }, 1); // 1.5s delay

      return () => clearTimeout(timer); // Cleanup on unmount
    }
  }, [isChallengeVisible]);

  useEffect(() => {
    if (isAnimLoading) {
      const totalAnimationDuration = 20000;
      const totalBarDuration = 6000;
      const stopTime1 = (co2Saved / maxCO2) * totalAnimationDuration;
      const stopTime2 = (co2Saved / maxCO2) * totalBarDuration;

      console.log('anim', treeLottieRef)
      const timeout1 = setTimeout(() => {
        treeLottieRef.current.pause();
      }, stopTime1);

      const timeout2 = setTimeout(() => {
        barLottieRef.current.pause();
      }, stopTime2);

      return () => {
        clearTimeout(timeout1);
        clearTimeout(timeout2);
      };
    }
  }, [isAnimLoading]);

  if (!challenges) return

  return (
    <Box>
      <Button
        onClick={toggleChallenge}
        className="!shadow-2xl"
        sx={{
          fontFamily: "Montserrat",
          borderRadius: "10px",
          zIndex: 99,
          backgroundColor: "orange",
          // width: 400,
          // height: 80,
          fontSize: 18,
          // animation: animate ? "pulse 1.3s infinite" : "none",
          // "@keyframes pulse": {
          //   "0%": { transform: "scale(1)" },
          //   "50%": { transform: "scale(1.1)" },
          //   "100%": { transform: "scale(1)" },
          // },
        }}
        // startIcon={<LuPartyPopper />}
        variant="contained"
      >
        <LuPartyPopper />
        {/* {t('challenge.button')} */}
      </Button>

      <Dialog
        open={isChallengeVisible}
        onClose={toggleChallenge}
        maxWidth="sm"
        fullWidth
        sx={{ backdropFilter: "blur(5px)" }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <DialogContent>
          {user && challenges.length > 0 && challenges[0].participants.length > 0 && challenges[0].participants.some(participant => participant.username === user.username) ? (
              <>
                <Typography variant="h5" fontWeight="bold" color="orange" mb={2}>
                  🦥 {challenges[0].title}
                </Typography>
                <Typography variant="h6" fontWeight="bold" mb={2}>
                  🏕️ {t('challenge.participants')}
                </Typography>
                <AvatarGroup max={8} sx={{ justifyContent: "center", mb: 2 }}>
                  {challenges[0].participants.map((user, index) => (
                    <Avatar key={index} src={user.profile_picture} alt={user.first_name} onClick={() => navigate(`/${user.username}`)} className="cursor-pointer">
                      {!user.profile_picture && (`${user.first_name.charAt(0).toUpperCase()}${user.last_name.charAt(0).toUpperCase()}`)}
                      </Avatar>
                  ))}
                </AvatarGroup>

                <Box textAlign="center" p={2} borderRadius={2} bgcolor="#f5f5f5" mb={2} position="relative">
      <IconButton
        onClick={() => setShowInfo(!showInfo)}
        sx={{ position: "absolute", top: 8, right: 8 }}
      >
        <InfoIcon />
      </IconButton>
      <Typography variant="h6">🌱 {t('challenge.co2-saved')}</Typography>
      <Box display="flex" justifyContent="center" alignItems="center" flexDirection="column">
      <ReactLottie
  animationData={treeAnimation}
  autoplay
  loop={false}
  style={{ width: 180, height: 100 }}
  lottieRef={treeLottieRef}
/>
    <Box display="flex" alignItems="center" gap={2}>
    <ReactLottie
  animationData={progressBarAnimation}
  autoplay
  loop={false}
  style={{ width: 120, height: 40 }}
  lottieRef={barLottieRef}
/>
      <Typography variant="h6" fontWeight="bold">
        {co2Saved} / {maxCO2} kg
      </Typography>
    </Box>
  </Box>

      <Collapse in={showInfo}>
        <Box mt={2} p={1} bgcolor="#e0e0e0" borderRadius={2}>
          <Typography variant="body2">
          {t('challenge.co2-saved-info')}
          <a href="https://lowtrip.fr/method" target="_blank" rel="noopener noreferrer">
  lowtrip.fr
</a>
          </Typography>
        </Box>
      </Collapse>
    </Box>
{/*
                <Box mt={4} mb={3} p={2} borderRadius={2} bgcolor="#e3f2fd">
        <p className="font-bold text-lg mb-2" >
          🏆  {t('challenge.criteria')}
        </p>
        <ul className="pl-10">
          <li className="pb-1 font-msr text-md">📸 <strong>{t('challenge.criteria-1-title')}</strong> – {t('challenge.criteria-1')}</li>
          <li className="pb-1 font-msr text-md">✍️ <strong>{t('challenge.criteria-2-title')}</strong> – {t('challenge.criteria-2')}</li>
          <li className="pb-1 font-msr text-md">🌧️ <strong>{t('challenge.criteria-3-title')}</strong> – {t('challenge.criteria-3')}</li>
        </ul>
      </Box> */}

      <p className="mb-2 font-bold text-xl">
                  🚴 {t('challenge.adventures')}
                </p>
                <div className="flex flex-row gap-2 flex-wrap justify-center">
                {challenges && challenges[0].participating_trips.map((trip, index) => (

<Link to={`/${trip.author?.username || "unknown"}/${trip.id}`} className="hover:scale-[1.02] transition-all">
<Card blog={trip} />
</Link>
                ))}
                </div>

                <Box bgcolor="#e3f2fd" p={3} mt={3} borderRadius={2}>
  <Typography variant="h6" fontWeight="bold" mb={2}>🏆 {t("challenge.criteria")}</Typography>
  <Box display="flex" justifyContent="space-evenly" flexWrap="wrap" gap={2}>
    {challenges[0].badges.map((badge, index) => (
      <Box
        key={index}
        onClick={() => {
          setSelectedBadge(badge);
          setShowModal(true);
        }}
        sx={{ cursor: "pointer" }}
      >
        <img
          src={badge.image_url}
          alt={badge.name}
          style={{ width: 80, borderRadius: 8 }}
        />
      </Box>
    ))}
  </Box>
</Box>

                <CommentSection
  challengeId={1}
/>

              </>
            ) : (
              <>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Typography variant="h5" fontWeight="bold" color="orange">
                     {t('challenge.title')}
                  </Typography>
                  <IconButton onClick={toggleChallenge}>
                    <CloseIcon />
                  </IconButton>
                </Box>
                <Typography variant="body1" paragraph>
                  🚴‍♀️ <strong>{t('challenge.info-1-title')}</strong>
                  <br />
                  {t('challenge.info-1a')} <strong>{t('challenge.info-1-rule')}</strong>
                  {t('challenge.info-1b')} 😉
                </Typography>
                <Typography variant="body1" paragraph>
                  🎉 <strong>{t('challenge.info-2-title')}</strong>{t('challenge.info-2a')}
                  <strong> {t('challenge.info-2-rule')}</strong>{t('challenge.info-2b')}
                </Typography>
                <Typography variant="body1" paragraph>
                  🏆 <strong>{t('challenge.info-3-title')}</strong>{t('challenge.info-3a')}
                   <strong>{t('challenge.info-3-rule')}</strong>
                   {t('challenge.info-3b')}
                </Typography>
                <Box display="flex" justifyContent="center" mt={3}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleJoinChallenge}
                    startIcon={<FaGripfire/>}
                    endIcon={<FaGripfire/>}
                    sx={{ borderRadius: "10px", padding: "10px 20px", backgroundColor: "orange" }}
                  >
{t('challenge.subscribe')}
                  </Button>
                </Box>
              </>
            )}
          </DialogContent>
        </motion.div>
      </Dialog>

      <Dialog open={showModal} onClose={() => setShowModal(false)} maxWidth="xs" fullWidth>
  {selectedBadge && (
    <DialogContent sx={{ textAlign: 'center', py: 4, display: 'flex', justifyContent: 'center', flexDirection:'column' }}>
      <img
        src={selectedBadge.image_url}
        alt="badge"
        style={{ width: 100, marginBottom: 16, alignSelf:'center' }}
      />
      <Typography variant="h6" fontWeight="bold" gutterBottom>
        {selectedBadge.name}
      </Typography>
      <Typography variant="body2">
        {selectedBadge.description}
      </Typography>
    </DialogContent>
  )}
</Dialog>

      <Modal isOpen={isOpen} onOpenChange={onOpenChange} size="xl">
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1 text-xl font-bold font-msr">{t('test-user-thanks')}</ModalHeader>
              <ModalBody className="pb-4">
                <p>{t('user-challenge')}</p>
                <CreateAccountForm setIsLoading={setIsLoading} redirection={false}/>
              </ModalBody>
            </>
          )}
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ChallengeButton;
